import { useState, useEffect } from "react";
import { CircularProgress, Box, Typography, Button } from "@mui/material";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import { initClientSide } from "./AppInitLogic";
import { setUserToken } from "./features/userSlice";
import { DynamicRoot } from "./core/layouts/Root/DynamicRoot";
import { useDeviceDetection } from "./customHooks/useDeviceDetection";

const AppInitializer = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const { isDetecting } = useDeviceDetection();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        setIsLoading(true);
        const token = await initClientSide((r: any) => r);
        dispatch(setUserToken(token));
      } catch (error) {
        console.error("App initialization failed:", error);
        Sentry.captureException(error);
        setInitError("Failed to initialize application");
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, [dispatch]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
          gap: 3,
        }}
      >
        <CircularProgress size={60} thickness={4} color="secondary" />
        <Typography variant="h6" color="textSecondary">
          Initializing Aladdin...
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Please wait
        </Typography>
      </Box>
    );
  }

  if (initError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
          gap: 2,
        }}
      >
        <Typography variant="h5" color="error">
          {initError}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Please refresh the page or contact support if the problem persists.
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
          color="secondary"
        >
          Refresh Page
        </Button>
      </Box>
    );
  }

  return isDetecting ? null : <DynamicRoot />;
};

export default AppInitializer;
